import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import PageHeader from '../components/PageHeader';

// Import images
import headerImage from '../assets/images/18.jpg';
import aboutImage from '../assets/images/Drum Institute/1.jpg';

// Import course images
import courseBeginner from '../assets/images/Drum Institute/4.jpg';
import courseAdvanced from '../assets/images/Drum Institute/3.jpg';

// Import certificate images
import certificate1 from '../assets/images/cetificates/1.jpeg';
import certificate2 from '../assets/images/cetificates/2.jpeg';
import certificate3 from '../assets/images/cetificates/3.jpeg';
import certificate4 from '../assets/images/cetificates/4.jpeg';

const InstitutePage: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [selectedCertificate, setSelectedCertificate] = useState<string | null>(null);

  const features = [
    {
      id: 1,
      title: "Professional Training",
      description: "Learn from government-approved qualified instructors with decades of experience",
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
        </svg>
      ),
    },
    {
      id: 2,
      title: "All Age Groups",
      description: "Programs designed for children, teenagers, and adults with different skill levels",
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      id: 3,
      title: "Award-Winning Programs",
      description: "Our students regularly win at national and international competitions",
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
    },
    {
      id: 4,
      title: "Fully Equipped Facilities",
      description: "Practice with professional instruments in our purpose-built studios",
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
  ];

  const courses = [
    {
      id: 1,
      title: "Beginner Drum Course",
      description: "Introduction to Sri Lankan drums for absolute beginners",
      duration: "6 months",
      skill: "Beginner",
      image: courseBeginner,
    },

    {
      id: 2,
      title: "Performance Mastery",
      description: "Elite training for performers seeking to master their craft",
      duration: "1 year",
      skill: "Advanced",
      image: courseAdvanced,
    },
  ];

  const certificates = [
    {
      id: 1,
      image: certificate1,
      title: "World Artistic Dance Federation",
      description: "Rivisara drum institute is a member of the World Artistic Dance Federation"
    },
    {
      id: 2,
      image: certificate2,
      title: "European association of Folklore Festivals",
      description: "International Instrumental Competition GOLDEN DRUM 2021"
    },
    {
      id: 3,
      image: certificate3,
      title: "American Arts & Education International Drum & Dance Competition 2022",
      description: "Award for outstanding international performances"
    },
    {
      id: 4,
      image: certificate4,
      title: "European association of Folklore Festivals",
      description: "International Instrumental Competition GOLDEN DRUM 2021"
    }
  ];

  const openCertificateModal = (imageSrc: string) => {
    setSelectedCertificate(imageSrc);
    document.body.style.overflow = 'hidden';
  };

  const closeCertificateModal = () => {
    setSelectedCertificate(null);
    document.body.style.overflow = 'auto';
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <>
      <PageHeader
        title="Rivisara Drum Institute"
        subtitle="Professional training for all ages in traditional Sri Lankan performing arts"
        backgroundImage={headerImage}
      >
        <Link
          to="/contact#booking"
          className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-white bg-orange-600 hover:bg-orange-700 transition-all shadow-lg"
        >
          Enroll Now
        </Link>
      </PageHeader>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold font-display text-gray-900 sm:text-4xl">
                About Our <span className="text-orange-600">Institute</span>
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Our Institute is a professional drum & dance studio specialized in training, grooming and 
                delivering excellent drummers & dancers of all ages. As a government-approved institution 
                with qualified trainers, we have won numerous awards at state, national, and international competitions.
              </p>
              <p className="mt-4 text-lg text-gray-600">
                We provide comprehensive education in traditional Sri Lankan performing arts, with a focus on 
                drumming techniques, dance forms, and cultural understanding. Our programs cater to all skill 
                levels from beginners to professional performers.
              </p>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="font-bold text-orange-600">20+</div>
                  <div className="text-sm text-gray-600">Years of Excellence</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="font-bold text-orange-600">500+</div>
                  <div className="text-sm text-gray-600">Students Trained</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="font-bold text-orange-600">50+</div>
                  <div className="text-sm text-gray-600">National Awards</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="font-bold text-orange-600">15+</div>
                  <div className="text-sm text-gray-600">International Recognitions</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="mt-12 lg:mt-0"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-orange-500 rounded-tl-3xl rounded-br-3xl transform -rotate-6"></div>
                <img
                  src={aboutImage}
                  alt="Students practicing at Rivisara Drum Institute"
                  className="relative z-10 rounded-tl-3xl rounded-br-3xl shadow-2xl"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

       {/* Certifications Section */}
       <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 sm:text-4xl">
              International <span className="text-orange-600">Certifications</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Our institute has been recognized globally for excellence in traditional Sri Lankan performing arts education
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {certificates.map((cert) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: cert.id * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer transform transition hover:-translate-y-1 hover:shadow-xl"
                onClick={() => openCertificateModal(cert.image)}
              >
                <div className="h-48 relative">
                  <img 
                    src={cert.image} 
                    alt={cert.title} 
                    className="w-full h-full object-contain p-2"
                  />
                  <div className="absolute inset-0 bg-black opacity-0 hover:opacity-10 transition-opacity"></div>
                </div>
                <div className="p-4 border-t">
                  <h3 className="font-bold text-gray-900">{cert.title}</h3>
                  <p className="mt-1 text-sm text-gray-600">{cert.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="text-gray-700">
              These certifications reflect our commitment to maintaining the highest standards in performing arts education
              and cultural preservation on a global scale.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Certificate Modal */}
      <AnimatePresence>
        {selectedCertificate && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
            onClick={closeCertificateModal}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh] overflow-hidden rounded-lg"
            >
              <button 
                className="absolute top-2 right-2 z-10 text-white bg-red-600 rounded-full p-2 shadow-lg"
                onClick={(e) => { e.stopPropagation(); closeCertificateModal(); }}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img 
                src={selectedCertificate} 
                alt="Certificate full view" 
                className="max-w-full max-h-[90vh] object-contain bg-white rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

     

      {/* Courses Section */}
      <section className="py-20 bg-white" ref={ref}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 sm:text-4xl">
              Our <span className="text-orange-600">Training Programs</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Discover the perfect course to start or advance your journey in Sri Lankan performing arts
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            {courses.map((course, index) => (
              <motion.div 
                key={course.id}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                className="bg-white rounded-xl shadow-xl overflow-hidden"
              >
                <div className="h-48 relative">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-0 right-0 bg-orange-600 text-white py-1 px-3 rounded-bl-lg">
                    {course.skill}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900">{course.title}</h3>
                  <p className="mt-2 text-gray-600">{course.description}</p>
                  <div className="mt-4 flex items-center text-sm text-orange-600">
                    <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Duration: {course.duration}
                  </div>
                  <div className="mt-6">
                    <Link
                      to="/contact#booking"
                      className="block w-full py-2 px-4 border border-orange-600 rounded-md text-center font-medium text-orange-600 hover:bg-orange-50"
                    >
                      Learn More
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-16 bg-orange-50 rounded-2xl p-8 text-center"
          >
            <h3 className="text-2xl font-bold text-gray-900">Looking for Custom Training?</h3>
            <p className="mt-2 text-gray-600 max-w-2xl mx-auto">
              We offer personalized training programs for individuals and groups. Contact us to discuss your specific needs.
            </p>
            <div className="mt-6">
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
              >
                Contact for Custom Programs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display text-gray-900 sm:text-4xl">
              Why Choose <span className="text-orange-600">Our Institute</span>
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              We provide a unique learning experience rooted in tradition while embracing modern teaching methods
            </p>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feature) => (
              <motion.div 
                key={feature.id}
                variants={itemVariants} 
                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="inline-block p-3 bg-orange-100 rounded-lg mb-4">
                  <div className="text-orange-600">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900">{feature.title}</h3>
                <p className="mt-2 text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-900 text-white relative">
        <div className="absolute inset-0 bg-pattern-dark opacity-10"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold font-display sm:text-4xl">
              What Our <span className="text-orange-400">Students Say</span>
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <p className="text-gray-300 italic">
                "Learning at Rivisara has transformed my understanding of Sri Lankan culture. The instructors are incredibly knowledgeable and passionate about passing on traditional techniques."
              </p>
              <div className="mt-4 flex items-center">
                <div className="h-10 w-10 rounded-full bg-orange-600 flex items-center justify-center font-bold">
                  DP
                </div>
                <div className="ml-3">
                  <p className="font-medium">Dinesh Perera</p>
                  <p className="text-sm text-gray-400">Student, 3 years</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-6"
            >
              <p className="text-gray-300 italic">
                "I enrolled my daughter when she was just 6 years old. The way the teachers work with children is amazing. She has developed confidence and a deep appreciation for our cultural heritage."
              </p>
              <div className="mt-4 flex items-center">
                <div className="h-10 w-10 rounded-full bg-orange-600 flex items-center justify-center font-bold">
                  SF
                </div>
                <div className="ml-3">
                  <p className="font-medium">Samanthi Fernando</p>
                  <p className="text-sm text-gray-400">Parent</p>
                </div>
              </div>
            </motion.div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Link
              to="/contact#booking"
              className="inline-flex items-center px-8 py-3 border border-orange-400 text-base font-medium rounded-full text-orange-400 hover:bg-orange-900/30 transition-all shadow-lg"
            >
              Join Our Community
              <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-16 bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:flex lg:items-center lg:justify-between">
            <div className="lg:max-w-xl">
              <h2 className="text-3xl font-bold font-display tracking-tight">
                Ready to start your journey with us?
              </h2>
              <p className="mt-3 text-lg text-orange-100">
                Enroll today and become part of our vibrant community of performers.
              </p>
            </div>
            <div className="mt-8 lg:mt-0 flex flex-col sm:flex-row sm:space-x-4">
              <Link
                to="/contact#booking"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-orange-600 bg-white hover:bg-orange-50 shadow-lg mb-4 sm:mb-0"
              >
                Enroll Now
              </Link>
              <Link
                to="/gallery"
                className="inline-flex items-center justify-center px-5 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-orange-700 shadow-lg"
              >
                View Gallery
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default InstitutePage;
